// ============================================================
// Creator Studio panel — Alpine store: inbox bell (web session)
// ============================================================

import Alpine from 'alpinejs';
import axios from '../../config/axios.js';
import { API_BASE } from '../../config/axios.js';
import {
  loadNotificationSoundPrefs,
  playNotificationSound,
} from '../../utils/notificationSound.js';

function creatorPrefix() {
  const raw = document.querySelector('meta[name="creator-url-prefix"]')?.content || '/studio';
  return raw.startsWith('/') ? raw.replace(/\/$/, '') : `/${raw}`.replace(/\/$/, '');
}

function resolveCreatorUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path) || path.startsWith('uspatih')) return path;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${creatorPrefix()}${p}`;
}

function isCreatorPanel() {
  return !!document.querySelector('meta[name="creator-url-prefix"]')?.content
    && !document.getElementById('kasir-app');
}

Alpine.store('creatorNotifications', {
  unread: 0,
  items: [],
  loading: false,
  open: false,
  _prevUnread: 0,
  _pollTimer: null,
  _prefsReady: false,

  async ensureSoundPrefs() {
    if (this._prefsReady) return;
    await loadNotificationSoundPrefs();
    this._prefsReady = true;
  },

  start() {
    if (!isCreatorPanel()) return;
    void this.ensureSoundPrefs();
    void this.fetchUnread();
    if (this._pollTimer) clearInterval(this._pollTimer);
    this._pollTimer = setInterval(() => { void this.fetchUnread(); }, 35_000);
  },

  async fetchUnread() {
    if (!isCreatorPanel()) return;
    try {
      await this.ensureSoundPrefs();
      const { data } = await axios.get(`${API_BASE}/notifications/unread-count`);
      const next = Number(data.data?.count ?? data.data?.unread_count ?? 0) || 0;
      if (next > this._prevUnread && this._prevUnread > 0) playNotificationSound();
      this._prevUnread = next;
      this.unread = next;
    } catch { /* silent */ }
  },

  async fetchRecent(limit = 10) {
    if (!isCreatorPanel()) return;
    this.loading = true;
    try {
      const { data } = await axios.get(`${API_BASE}/notifications`, { params: { per_page: limit } });
      this.items = Array.isArray(data.data) ? data.data : [];
      if (typeof data.unread_count === 'number') {
        this.unread = data.unread_count;
        this._prevUnread = data.unread_count;
      }
    } catch {
      this.items = [];
    } finally {
      this.loading = false;
    }
  },

  async toggle() {
    this.open = !this.open;
    if (this.open) await this.fetchRecent();
  },

  close() {
    this.open = false;
  },

  async markRead(id) {
    if (!id) return;
    try {
      await axios.post(`${API_BASE}/notifications/${id}/read`);
      const item = this.items.find((n) => String(n.id) === String(id));
      if (item && !item.is_read) {
        item.is_read = true;
        this.unread = Math.max(0, this.unread - 1);
        this._prevUnread = this.unread;
      }
    } catch { /* silent */ }
  },

  async markAllRead() {
    try {
      await axios.post(`${API_BASE}/notifications/read-all`);
      this.items.forEach((n) => { n.is_read = true; });
      this.unread = 0;
      this._prevUnread = 0;
    } catch { /* silent */ }
  },

  go(item) {
    if (!item) return;
    if (!item.is_read) void this.markRead(item.id);
    this.close();

    const data = item.data || {};
    if (data.web_url && String(data.web_url).startsWith('/')) {
      window.location.href = resolveCreatorUrl(String(data.web_url)) || String(data.web_url);
      return;
    }

    let target = item.action_url || data.url || data.deep_link || null;
    if (target && (String(target).startsWith('uspatih') || /^https?:\/\//i.test(target))) {
      const t = String(target).toLowerCase();
      if (t.includes('revenue')) target = '/dashboard';
      else if (t.includes('studio')) target = '/dashboard';
      else if (t.includes('level') || t.includes('xp') || t.includes('points') || t.includes('quest') || t.includes('poin')) {
        target = '/dashboard';
      } else {
        target = '/notifications';
      }
    }
    if (!target) target = '/notifications';

    const href = resolveCreatorUrl(target);
    if (href) window.location.href = href;
  },
});

document.addEventListener('alpine:init', () => {
  queueMicrotask(() => Alpine.store('creatorNotifications').start());
});

document.addEventListener('DOMContentLoaded', () => {
  if (window.Alpine?.store) Alpine.store('creatorNotifications').start();
});
